import React, { useState, useRef, useEffect, ChangeEvent } from 'react';

interface Option {
  value: string;
  label: string;
}

type DateInputPickerProps = {
  value: number;
  selectedUnit: string;
  options: Option[];
  onValueChange: (n: number) => void;
  onUnitChange: (unit: string) => void;
  disabled?: boolean;
};

const DateInputPicker = (props: DateInputPickerProps) => {
  const { value, selectedUnit, options, onValueChange, onUnitChange, disabled = false } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(String(value));
  const btnGroupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInputValue(String(value));
  }, [value]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (btnGroupRef.current && !btnGroupRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleValueChange = (e: ChangeEvent<HTMLInputElement>) => {
    const txt = e.target.value;
    setInputValue(txt);
    if (txt.trim() === '') {
      return;
    }
    const n = parseInt(txt, 10);
    onValueChange(n);
  };

  const displayLabel =
    options.find((o) => o.value === selectedUnit || o.label === selectedUnit)?.label ?? '';

  return (
    <div className="inline-module no-margin">
      <div className="input-group is-sm">
        <input
          className="form-control"
          type="number"
          placeholder="0"
          value={inputValue}
          onChange={handleValueChange}
          disabled={disabled}
        />
        <div className="input-group-btn">
          <div
            className={`btn-group dropdown${isOpen ? ' open' : ''}`}
            ref={btnGroupRef}
            role="combobox"
            aria-expanded={isOpen}
            onKeyDown={(e) => e.key === 'Escape' && setIsOpen(false)}
          >
            <button
              type="button"
              className="btn dropdown-toggle btn-default"
              onClick={() => {
                if (!disabled) {
                  setIsOpen((o) => !o);
                }
              }}
              disabled={disabled}
            >
              <span className="ng-binding">{displayLabel}</span>
              <span className="caret" />
            </button>

            {!disabled && isOpen && (
              <ul className="dropdown-menu" role="listbox">
                {options.map((opt) => (
                  <li
                    key={opt.value}
                    role="option"
                    className={opt.value === selectedUnit ? 'active' : ''}
                    onClick={(e) => {
                      e.preventDefault();
                      onUnitChange(opt.value);
                      setIsOpen(false);
                    }}
                  >
                    <a href="#">{opt.label}</a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DateInputPicker;
